import * as React from 'react';

import { clamp, isEqual, round } from 'utils';

const Actions = {
  CurrentSlideChanged: 'CurrentSlideChanged',
  OptionsChanged: 'OptionsChanged',
  PlayStateChanged: 'PlayStateChanged',
  SlidesChanged: 'SlidesChanged',
  SlidesContainerChanged: 'SlidesContainerChanged',
  StateChanged: 'StateChanged',
  UserStateChanged: 'UserStateChanged',
};

function getInitialState(options = {}) {
  const autoplayDefaults = {
    interval: 5000,
    pauseOnHover: true,
  };

  const autoplay =
    typeof options.autoplay === 'object'
      ? { ...autoplayDefaults, ...options.autoplay }
      : autoplayDefaults;

  return {
    current: -1,
    isEnd: true,
    isMouseIn: false,
    isPaused: !options.autoplay,
    isStart: true,
    offset: { end: undefined, start: undefined },
    options: {
      alignSlides: 'stretch',
      autoplayIsEnabled: Boolean(options.autoplay),
      autoplayInterval: autoplay.interval,
      autoplayPauseOnHover: autoplay.pauseOnHover,
      clickToSlide: false,
      gap: undefined,
      initialSlide: 0,
      justifySlides: 'center',
      mode: 'slide',
      offset: undefined,
      ...options,
    },
    originalOptions: options,
    slides: undefined,
    slidesContainer: undefined,
  };
}

/**
 * This function contains the logic for determining various parts of the
 * carousel's state (when in "slide" mode).
 *
 * In slide mode the carousel is "uncontrolled", meaning we don't change
 * slide or scroll positions programmatically. Rather, the user controls the
 * carousel position and we update state accordingly.
 */
function getComputedState(prev = {}) {
  const { options, slides, slidesContainer } = prev;

  if (!slidesContainer || !slides || options.mode !== 'slide') {
    return prev;
  }

  const next = { ...prev };

  const containerStart = slidesContainer.scrollLeft;
  const containerEnd = slidesContainer.scrollLeft + slidesContainer.clientWidth;

  // Create an array of all slides that are at least partially visible.
  const intersecting = slides
    .map((el) => {
      const start = el.offsetLeft;
      const end = el.offsetLeft + el.clientWidth;
      const visibleWidth =
        Math.min(end, containerEnd) - Math.max(start, containerStart);
      return {
        target: el,
        isIntersecting: start < containerEnd && end > containerStart,
        intersectionRatio: Math.max(visibleWidth / el.clientWidth, 0),
        center: (start + end) / 2,
      };
    })
    .filter((o) => o.isIntersecting);

  next.current = -1;

  if (intersecting.length > 0) {
    if (options?.justifySlides === 'start') {
      // When justifySlides === 'start' the current slide is the first one
      // that's "most" visible.
      const intersection =
        intersecting.find((o) => o.intersectionRatio >= 1) ||
        intersecting.reduce((a, b) =>
          b.intersectionRatio > a.intersectionRatio ? b : a
        );
      next.current = slides.indexOf(intersection?.target);
    } else {
      // When justifySlides === 'center' the current slide is one that has it's
      // center closest to the center of the container
      const center = (containerStart + containerEnd) / 2;
      const intersection = intersecting.reduce((a, b) =>
        Math.abs(center - b.center) < Math.abs(center - a.center) ? b : a
      );
      next.current = slides.indexOf(intersection?.target);
    }
  }

  next.isStart = next.current === 0 || containerStart === 0;
  next.isEnd =
    next.current === slides.length - 1 ||
    containerEnd >= slidesContainer.scrollWidth;

  // Thes offsets are used to ensure that the first and last slide may be
  // scrolled to the center of the container.
  if (options.justifySlides === 'center') {
    const first = slides[0];
    const last = slides[slides.length - 1];
    next.offset.start = (slidesContainer.clientWidth - first.clientWidth) / 2;
    next.offset.end = (slidesContainer.clientWidth - last.clientWidth) / 2;
  }

  return next;
}

function reducer(prev, [type, payload]) {
  let next = { ...prev };
  // eslint-disable-next-line default-case
  switch (type) {
    case Actions.CurrentSlideChanged: {
      if (prev.current !== payload.current) {
        next = getComputedState({ ...prev, current: payload.current });
      }
      break;
    }
    case Actions.OptionsChanged: {
      const { options } = getInitialState(payload.options);
      if (prev.options !== options) {
        next = getComputedState({
          ...prev,
          options: { ...prev.options, ...options },
        });
      }
      break;
    }
    case Actions.PlayStateChanged: {
      if (prev.isPaused !== payload.isPaused) {
        next = { ...prev, isPaused: payload.isPaused };
      }
      break;
    }
    case Actions.SlidesChanged: {
      if (!isEqual(prev.slides, payload.slides)) {
        next = getComputedState({ ...prev, slides: payload.slides });
      }
      break;
    }
    case Actions.SlidesContainerChanged: {
      if (prev.slidesContainer !== payload.slidesContainer) {
        next = getComputedState({
          ...prev,
          slidesContainer: payload.slidesContainer,
        });
      }
      break;
    }
    case Actions.StateChanged: {
      next = getComputedState(prev);
      break;
    }
    case Actions.UserStateChanged: {
      next = { ...prev, ...payload };
      break;
    }
  }
  return isEqual(prev, next) ? prev : next;
}

export default function useCarousel(options = {}) {
  const [state, dispatch] = React.useReducer(reducer, options, getInitialState);

  const intersectionObserver = React.useRef();
  const mutationObserver = React.useRef();
  const resizeObserver = React.useRef();
  const scrollendListener = React.useRef();

  const isInitialized = React.useRef(false);
  const isReady = Boolean(state.slidesContainer && state.slides);

  const methods = React.useRef({});

  // Autoplay functions and related references are kept in a single, persistant
  // object. This is tidier than having separate refs for each property.
  const autoplayController = React.useRef({
    callback() {},
    index: -1,
    observers: new Set(),
    progress: 0,
    ref: null,
    start() {
      this.timestamp =
        window.performance.now() -
        this.progress * state.options.autoplayInterval;
      this.ref = requestAnimationFrame(this.callback.bind(this));
    },
    stop() {
      if (this.ref) {
        cancelAnimationFrame(this.ref);
        this.ref = null;
      }
    },
    timestamp: null,
  });

  methods.current = {
    next(options) {
      this.turn(state.current + 1, options);
    },
    pause() {
      dispatch([Actions.PlayStateChanged, { isPaused: true }]);
    },
    play() {
      dispatch([Actions.PlayStateChanged, { isPaused: false }]);
    },
    previous(options) {
      this.turn(state.current - 1, options);
    },
    stop() {
      dispatch([Actions.PlayStateChanged, { isPaused: true }]);
      autoplayController.current.progress = 0;
    },
    turn(
      index = state.current + 1,
      { behavior = 'smooth', loop = false } = {}
    ) {
      if (state.slidesContainer && state.slides) {
        const first = 0;
        const last = state.slides.length - 1;
        let i = index;
        if (loop) {
          i = i < first ? last : i > last ? first : i;
        } else {
          i = Math.min(last, Math.max(first, i));
        }
        const slide = state.slides[i];
        if (slide && state.options.mode === 'fade') {
          dispatch([Actions.CurrentSlideChanged, { current: i }]);
        }
        if (slide && state.options.mode === 'slide') {
          const left =
            state.options.justifySlides === 'start'
              ? slide.offsetLeft - state.slides[0].offsetLeft
              : slide.offsetLeft -
                (state.slidesContainer.clientWidth - slide.clientWidth) / 2;
          state.slidesContainer.scroll({ left, behavior });
        }
      }
    },
  };

  // TODO Swap this autoplay logic for the newer useInterval() hook.

  // The autoplay frame request callback function is defined separately.
  // Defining the function on every render ensures that it always has access to
  // the latest state.
  autoplayController.current.callback = function autoplayCallback(now) {
    const interval = state.options.autoplayInterval;
    // Reset the clock when the current slide changes.
    if (!this.timestamp || this.index !== state.current) {
      this.timestamp = performance.now();
    }
    // Keep track of the current slide to detect slide changes.
    this.index = state.current;
    // Calculate the elapsed time for the current slide.
    const delta = now - this.timestamp;
    if (delta < interval) {
      // If the elapsed time is less than the specified interval, then request
      // a new frame after updating the progress value and calling the user-
      // provided callbacks.
      this.progress = clamp(0, round(delta / interval), 1);
      this.observers.forEach((fn) => fn(this.progress));
      this.ref = requestAnimationFrame(this.callback.bind(this));
    } else {
      // If the elapsed time is equal to or greater than the specified
      // interval, then reset our references and turn to the next slide.
      this.ref = null;
      this.timestamp = now;
      this.progress = 0;
      this.observers.forEach((fn) => fn(this.progress));
      methods.current?.next({ loop: true });
    }
  };

  const autoplayProgress = React.useRef({
    getValue() {
      return autoplayController.current.progress;
    },
    subscribe(fn) {
      autoplayController.current.observers.add(fn);
      return () => {
        autoplayController.current.observers.delete(fn);
      };
    },
  });

  // Turn to the initial slide on initialization.
  if (isReady && !isInitialized.current) {
    requestAnimationFrame(() => {
      methods.current?.turn(state.options.initialSlide, {
        behavior: 'instant',
      });
    });
  }

  // Update state with new options as props change.
  React.useEffect(() => {
    if (!isEqual(state.originalOptions, options)) {
      dispatch([Actions.OptionsChanged, { options }]);
    }
  }, [state.originalOptions, options]);

  // Register observers and listeners that handle scroll-based state changes.
  React.useEffect(() => {
    // Abort if we don't yet have a container ref.
    if (!state.slidesContainer) {
      return;
    }

    // Update slide references on initialization.
    if (!isInitialized.current) {
      dispatch([
        Actions.SlidesChanged,
        { slides: Array.from(state.slidesContainer.children) },
      ]);
    }

    // Updates slide references when slides are added or removed from the DOM.
    if (!mutationObserver.current && 'MutationObserver' in window) {
      mutationObserver.current = new MutationObserver((entries) => {
        entries.forEach(({ target }) => {
          if (target instanceof HTMLElement) {
            dispatch([
              Actions.SlidesChanged,
              { slides: Array.from(state.slidesContainer.children) },
            ]);
          }
        });
      });
    }
    mutationObserver.current.observe(state.slidesContainer, {
      childList: true,
    });

    if (state.options.mode === 'slide') {
      // Update state on scrollend events.
      if (!scrollendListener.current) {
        scrollendListener.current = () => {
          dispatch([Actions.StateChanged]);
        };
      }
      state.slidesContainer.addEventListener(
        'scrollend',
        scrollendListener.current
      );

      // Update state when the slides container is resized.
      if (!resizeObserver.current && 'ResizeObserver' in window) {
        resizeObserver.current = new ResizeObserver(() => {
          dispatch([Actions.StateChanged]);
        });
      }
      resizeObserver.current.observe(state.slidesContainer);

      // Update state on slide visibility changes.
      if (!intersectionObserver.current && 'IntersectionObserver' in window) {
        intersectionObserver.current = new IntersectionObserver(
          () => {
            dispatch([Actions.StateChanged]);
          },
          { root: state.slidesContainer, threshold: [0, 0.5, 1] }
        );
      }
      state.slides?.forEach((el) => intersectionObserver.current?.observe(el));
    }
  }, [state.options, state.slides, state.slidesContainer]);

  // Start or stop autoplay when state changes.
  React.useEffect(() => {
    if (isReady) {
      const isEnabled =
        state.options.autoplayIsEnabled &&
        !state.isPaused &&
        !(state.options.autoplayPauseOnHover && state.isMouseIn);
      // Start autoplay when (1) autoplay is enabled, (2) the carousel is not
      // paused, and (3) the carousel is not currently playing.
      if (isEnabled && !autoplayController.current.ref) {
        autoplayController.current.start();
      }
      // Stop autoplay when the carousel is playing and (1) autoplay is not
      // enabled _or_ (2) the carousel is paused.
      if (!isEnabled && autoplayController.current.ref) {
        autoplayController.current.stop();
      }
    }
    // Including state.current forces the hook to run on every slide change.
  }, [isReady, state.current, state.isMouseIn, state.isPaused, state.options]);

  // Reset the autoplay progress indicator whenever the current slide changes
  React.useEffect(() => {
    if (state.options.autoplayIsEnabled) {
      autoplayController.current.progress = 0;
      autoplayController.current.observers.forEach((fn) =>
        fn(autoplayController.current.progress)
      );
    }
    // Including state.current forces the hook to run on every slide change.
  }, [state.current, state.options]);

  // Set the initialization indicator.
  isInitialized.current = isReady;

  return {
    autoplayProgress: autoplayProgress.current,
    getSlidesContainerProps: (props = {}) => ({
      ...props,
      onMouseOver: (event) => {
        if (event.currentTarget !== event.target) {
          dispatch([Actions.UserStateChanged, { isMouseIn: true }]);
        }
      },
      onMouseOut: (event) => {
        if (
          event.currentTarget === event.relatedTarget ||
          !event.currentTarget.contains(event.relatedTarget)
        ) {
          dispatch([Actions.UserStateChanged, { isMouseIn: false }]);
        }
      },
      ref: (el) => {
        if (el instanceof HTMLElement && el !== state.slidesContainer) {
          dispatch([Actions.SlidesContainerChanged, { slidesContainer: el }]);
        }
      },
    }),
    next: methods.current.next,
    pause: methods.current.pause,
    play: methods.current.play,
    previous: methods.current.previous,
    stop: methods.current.stop,
    state,
    turn: methods.current.turn,
  };
}
